/** Các bảng của thẻ «Data & chốt». Thẻ điện thoại: chỉ tên là dòng đầu, mọi số đều có nhãn. */
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";
import { ChartFrame } from "@/components/ChartFrame";
import { DataTable } from "@/components/DataTable";
import { ChartCard } from "@/features/dashboard/primitives/ChartCard";
import { ChartTip } from "@/features/dashboard/primitives/ChartTip";
import { intVN } from "@/features/dashboard/lib/format";
import { readToken } from "@/features/dashboard/lib/palette";
import { formatVNDCompact } from "@/lib/format";
import type { SalesChatMetrics, SalesChatNumbers } from "../api";
import { funnelPct, hoursText, maybeInt, pctText, secondsText } from "../dataChot";

type SaleRow = SalesChatNumbers & { key: string; name: string; note?: string; dim?: boolean; total?: boolean };

function saleRows(m: SalesChatMetrics): SaleRow[] {
  const rows: SaleRow[] = m.people.map((p) => ({ ...p, key: p.staffId, name: p.name, note: p.linked ? p.squadName : "Chưa ghép tài khoản Pancake" }));
  for (const u of m.unmatched) rows.push({ ...u, key: `u:${u.pancakeUid}`, name: u.displayName || u.pancakeUid, note: "Chưa ghép", dim: true });
  if (m.unassigned) rows.push({ ...m.unassigned, key: "unassigned", name: "Chưa giao", dim: true });
  if (m.limited) {
    for (const s of m.squads) rows.push({ ...s, key: `s:${s.squadId}`, name: `Tổng ${s.name}`, total: true });
  } else {
    rows.push({ ...m.totals, key: "total", name: "Tổng", total: true });
  }
  return rows;
}

export function SaleTable({ m }: { m: SalesChatMetrics }) {
  const columns = useMemo<ColumnDef<SaleRow>[]>(() => {
    // Thẻ điện thoại: chỉ tên là dòng đầu, mọi số đều có nhãn — số trần cạnh tên không đọc được là số gì.
    const num = (id: string, header: string, get: (r: SaleRow) => string): ColumnDef<SaleRow> => ({
      id,
      header,
      cell: ({ row }) => <span className="tabular-nums">{get(row.original)}</span>,
      meta: { mobilePriority: "secondary", mobileLabel: header },
    });
    return [
      {
        id: "name",
        header: "Sale",
        cell: ({ row }) => (
          <span className={row.original.total ? "font-semibold" : row.original.dim ? "text-muted" : "font-medium"}>
            {row.original.name}
            {row.original.note ? <span className="ml-1 text-xs font-normal text-muted">{row.original.note}</span> : null}
          </span>
        ),
        meta: { mobilePriority: "primary" },
      },
      num("dataNew", "Data mới", (r) => maybeInt(r.dataNew)),
      num("dataReturning", "Data cũ", (r) => maybeInt(r.dataReturning)),
      num("quotes", "Qua giá", (r) => maybeInt(r.quotes)),
      num("deposits", "Chờ cọc", (r) => maybeInt(r.deposits)),
      num("closedHn", "Chốt HN", (r) => maybeInt(r.closedHn)),
      num("closedSg", "Chốt SG", (r) => maybeInt(r.closedSg)),
      num("closed", "Tổng chốt", (r) => maybeInt(r.closed)),
      num("closedByTag", "Theo thẻ", (r) => maybeInt(r.closedByTag)),
      num("closeRatePct", "Tỉ lệ chốt", (r) => pctText(r.closeRatePct)),
      num("revenue", "Doanh số", (r) => (r.revenue == null ? "—" : formatVNDCompact(r.revenue))),
      num("replyPct", "15 phút", (r) => pctText(r.replyPct)),
      num("respMedianSeconds", "Phản hồi giữa", (r) => secondsText(r.respMedianSeconds)),
      num("activeMinutes", "Giờ hoạt động", (r) => hoursText(r.activeMinutes)),
    ];
  }, []);
  return (
    <section aria-labelledby="dc-theo-sale" className="space-y-2">
      <h3 id="dc-theo-sale" className="text-base font-semibold">
        Theo sale
      </h3>
      <p className="text-xs text-muted">
        {m.limited
          ? "Bạn xem được số của mình và tổng tiểu đội."
          : 'Data tính cho người được giao cuối ngày. Chốt lấy từ đơn thật; "Theo thẻ" để đối chiếu.'}
      </p>
      <DataTable columns={columns} data={saleRows(m)} mobileVariant="stack" emptyMessage="Chưa có số trong phạm vi bạn được xem." />
    </section>
  );
}

export function PageTable({ m }: { m: SalesChatMetrics }) {
  const columns = useMemo<ColumnDef<SalesChatMetrics["pages"][number]>[]>(
    () => [
      {
        id: "name",
        header: "Trang",
        cell: ({ row }) => <span className="font-medium">{row.original.pageName || row.original.pageId}</span>,
        meta: { mobilePriority: "primary" },
      },
      {
        id: "dataNew",
        header: "Mới",
        cell: ({ row }) => <span className="tabular-nums">{maybeInt(row.original.dataNew)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Mới" },
      },
      {
        id: "dataReturning",
        header: "Cũ",
        cell: ({ row }) => <span className="tabular-nums">{maybeInt(row.original.dataReturning)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Cũ" },
      },
      {
        id: "closedByTag",
        header: "Chốt thẻ",
        cell: ({ row }) => <span className="tabular-nums">{maybeInt(row.original.closedByTag)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Chốt thẻ" },
      },
      {
        id: "rate",
        header: "Tỉ lệ",
        cell: ({ row }) => <span className="tabular-nums">{pctText(funnelPct(row.original.closedByTag, row.original.dataNew))}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Tỉ lệ" },
      },
    ],
    [],
  );
  return (
    <section aria-labelledby="dc-theo-trang" className="min-w-0 space-y-2">
      <h3 id="dc-theo-trang" className="text-base font-semibold">
        Theo trang
      </h3>
      <p className="text-xs text-muted">Đơn không ghi khách đến từ trang nào nên bảng này chỉ có thẻ Pancake.</p>
      <DataTable columns={columns} data={m.pages} mobileVariant="stack" emptyMessage="Chưa có số trang trong kỳ." />
    </section>
  );
}

export function BranchTable({ m }: { m: SalesChatMetrics }) {
  const monthLabel = `Từ đầu tháng ${m.to.slice(5, 7)}`;
  const columns = useMemo<ColumnDef<SalesChatMetrics["branches"][number]>[]>(
    () => [
      {
        id: "name",
        header: "Cơ sở",
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
        meta: { mobilePriority: "primary" },
      },
      {
        id: "range",
        header: "Trong kỳ",
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatVNDCompact(row.original.rangeRevenue)} · {maybeInt(row.original.rangeOrders)} đơn
          </span>
        ),
        meta: { mobilePriority: "secondary", mobileLabel: "Trong kỳ" },
      },
      {
        id: "month",
        header: monthLabel,
        cell: ({ row }) => (
          <span className="tabular-nums">
            {formatVNDCompact(row.original.monthRevenue)} · {maybeInt(row.original.monthOrders)} đơn
          </span>
        ),
        meta: { mobilePriority: "secondary", mobileLabel: monthLabel },
      },
    ],
    [monthLabel],
  );
  return (
    <section aria-labelledby="dc-co-so" className="min-w-0 space-y-2">
      <h3 id="dc-co-so" className="text-base font-semibold">
        Doanh số chốt theo cơ sở
      </h3>
      <p className="text-xs text-muted">Theo ngày tạo đơn, khác "Doanh thu thực tế" (khi khách trả đủ) và "Doanh thu dự kiến" (theo ngày chụp).</p>
      <DataTable columns={columns} data={m.branches} mobileVariant="stack" emptyMessage="Chưa có đơn chốt trong kỳ." />
    </section>
  );
}

export function FunnelCard({ m }: { m: SalesChatMetrics }) {
  const f = m.funnel;
  if (!f) return null;
  const brand = readToken("--color-brand", "#8b6914");
  const data = [
    { label: "Data mới", value: f.dataNew },
    { label: "Tiềm năng", value: f.prospects },
    { label: "Qua giá", value: f.quotes },
    { label: "Gần chốt", value: f.deposits },
    { label: "Chốt theo thẻ", value: f.closedByTag },
  ];
  // Sale có thể gắn thẳng thẻ chốt, bỏ qua nấc trước: quá 100% không phải tỉ lệ còn lại, nên không ghi.
  const buoc = data.slice(1).flatMap((d, i) => {
    const pct = funnelPct(d.value, data[i]?.value ?? 0);
    return pct == null || pct > 100 ? [] : [`${d.label} ${pctText(pct)}`];
  });
  return (
    <ChartCard title="Phễu trong kỳ" subtitle="Theo thẻ Pancake">
      <ChartFrame height={200}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={88} />
          <Tooltip content={<ChartTip formatter={(v) => intVN(v)} />} />
          <Bar dataKey="value" name="Khách" fill={brand} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ChartFrame>
      <p className="mt-2 text-xs text-muted">
        Còn lại so với nấc trước: {buoc.join(" · ")}
      </p>
      <p className="mt-1 text-xs text-muted">
        Chê đắt {maybeInt(f.tooExpensive)} · Mất {maybeInt(f.lost)} · Chuyển thương hiệu {maybeInt(f.otherBrand)}
      </p>
    </ChartCard>
  );
}

export function CohortTable({ m }: { m: SalesChatMetrics }) {
  const columns = useMemo<ColumnDef<SalesChatMetrics["cohorts"][number]>[]>(() => {
    const num = (id: "dataNew" | "closed1d" | "closed3d" | "closed7d" | "closed30d", header: string) =>
      ({
        id,
        header,
        cell: ({ row }) => <span className="tabular-nums">{maybeInt(row.original[id])}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: header },
      }) satisfies ColumnDef<SalesChatMetrics["cohorts"][number]>;
    return [
      {
        id: "brand",
        header: "Thương hiệu",
        cell: ({ row }) => <span className="font-medium">{row.original.brand}</span>,
        meta: { mobilePriority: "primary" },
      },
      num("dataNew", "Khách"),
      num("closed1d", "1 ngày"),
      num("closed3d", "3 ngày"),
      num("closed7d", "7 ngày"),
      num("closed30d", "30 ngày"),
    ];
  }, []);
  return (
    <section aria-labelledby="dc-lua" className="min-w-0 space-y-2">
      <h3 id="dc-lua" className="text-base font-semibold">
        Chốt theo lứa khách
      </h3>
      <p className="text-xs text-muted">Khách vào trong kỳ, sau bao lâu thì chốt, theo thẻ Pancake. Ngày gần đây chưa đủ thời gian nên số còn tăng.</p>
      <DataTable columns={columns} data={m.cohorts} mobileVariant="stack" emptyMessage="Chưa có số lứa khách trong kỳ." />
    </section>
  );
}

export function SquadTable({ m }: { m: SalesChatMetrics }) {
  const columns = useMemo<ColumnDef<SalesChatMetrics["squads"][number]>[]>(
    () => [
      {
        id: "name",
        header: "Tiểu đội",
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
        meta: { mobilePriority: "primary" },
      },
      {
        id: "dataNew",
        header: "Data mới",
        cell: ({ row }) => <span className="tabular-nums">{maybeInt(row.original.dataNew)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Data mới" },
      },
      {
        id: "closed",
        header: "Tổng chốt",
        cell: ({ row }) => <span className="tabular-nums">{maybeInt(row.original.closed)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Tổng chốt" },
      },
      {
        id: "rate",
        header: "Tỉ lệ chốt",
        cell: ({ row }) => <span className="tabular-nums">{pctText(row.original.closeRatePct)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Tỉ lệ chốt" },
      },
      {
        id: "revenue",
        header: "Doanh số",
        cell: ({ row }) => <span className="tabular-nums">{row.original.revenue == null ? "—" : formatVNDCompact(row.original.revenue)}</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Doanh số" },
      },
    ],
    [],
  );
  return (
    <section aria-labelledby="dc-tieu-doi" className="space-y-2">
      <h3 id="dc-tieu-doi" className="text-base font-semibold">
        Theo tiểu đội
      </h3>
      <DataTable columns={columns} data={m.squads} mobileVariant="stack" emptyMessage="Chưa có số." />
    </section>
  );
}
